import React from "react";
import Slider from "../../Components/Slider/Slider";
import DatePicker from "../../Components/DatePicker/DatePicker";
import Loader from "../../Components/Loader/Loader";
import API from "../../API/REST_API";

// Color palette for categories - consistent colors based on category name
const categoryColorPalette = [
  "#3b82f6", // blue
  "#a855f7", // purple
  "#6366f1", // indigo
  "#14b8a6", // teal
  "#f59e0b", // amber
  "#f43f5e", // rose
  "#10b981", // emerald
  "#06b6d4", // cyan
  "#ec4899", // pink
  "#f97316", // orange
  "#8b5cf6", // violet
  "#0ea5e9", // sky
];

// Generate consistent color for a category based on its name
const getCategoryColor = (categoryName) => {
  if (!categoryName) return categoryColorPalette[0];
  
  // Simple hash function to convert category name to a consistent index
  let hash = 0;
  for (let i = 0; i < categoryName.length; i++) {
    const char = categoryName.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  
  const index = Math.abs(hash) % categoryColorPalette.length;
  return categoryColorPalette[index];
};

// Round number to 2 decimal places to remove floating point errors
const roundNumber = (num) => {
  return Math.round(num * 100) / 100;
};

// Clean array of numbers by rounding each to 2 decimals
const cleanNumberArray = (arr) => {
  if (!Array.isArray(arr)) return arr;
  return arr.map(val => typeof val === 'number' ? roundNumber(val) : val);
};

export default class Reports extends React.Component {
  state = {
    pieChart: {
      options: {
        title: {
          text: "Expenses Breakdown",
          align: "left",
          margin: 2,
          offsetX: 0,
          offsetY: 0,
          floating: false,
          style: {
            fontSize: "20px",
            fontWeight: "bold",
            fontFamily: undefined,
            color: "#053a4b",
          },
        },
        chart: {
          id: "basic-bar",
          foreColor: "black",
        },
        tooltip: {
          y: {
            formatter: function (val) {
              // Convert to number and round to 2 decimals to remove floating point errors
              const numVal = typeof val === 'number' ? val : parseFloat(val);
              if (isNaN(numVal)) return val;
              
              // Round to 2 decimals to avoid floating point precision issues
              const rounded = Math.round(numVal * 100) / 100;
              
              return new Intl.NumberFormat("en-US", {
                maximumFractionDigits: 2,
                minimumFractionDigits: 0,
              }).format(rounded);
            },
          },
        },
        labels: ["Income", "Express", "Saving"],
      },
      series: [65, 55, 20],
      width: 300,
      type: "pie",
      title: "",
    },
    radialBar: {
      options: {
        title: {
          text: "Revenue Vs Expense ",
          align: "left",
          margin: 2,
          offsetX: 0,
          offsetY: 0,
          floating: false,
          style: {
            fontSize: "20px",
            fontWeight: "bold",
            fontFamily: undefined,
            color: "#053a4b",
          },
        },
        dataLabels: {
          enabled: true,
          formatter: function (val) {
            // Convert to number and round to 2 decimals to remove floating point errors
            const numVal = typeof val === 'number' ? val : parseFloat(val);
            if (isNaN(numVal)) return val;
            
            // Round to 2 decimals to avoid floating point precision issues
            const rounded = Math.round(numVal * 100) / 100;
            
            return new Intl.NumberFormat("en-US", {
              maximumFractionDigits: 2,
              minimumFractionDigits: 0,
            }).format(rounded);
          },
          style: {
            fontSize: "12px",
            fontFamily: "Helvetica, Arial, sans-serif",
            fontWeight: "bold",
          },
        },
        chart: {
          foreColor: "#053a4b",
          redrawOnParentResize: true,
          toolbar: {
            show: false,
          },
        },
        tooltip: {
          y: {
            formatter: function (val) {
              // Convert to number and round to 2 decimals to remove floating point errors
              const numVal = typeof val === 'number' ? val : parseFloat(val);
              if (isNaN(numVal)) return val;
              
              // Round to 2 decimals to avoid floating point precision issues
              const rounded = Math.round(numVal * 100) / 100;
              
              return new Intl.NumberFormat("en-US", {
                maximumFractionDigits: 2,
                minimumFractionDigits: 0,
              }).format(rounded);
            },
          },
        },
        colors: ["#10b981", "#ef4444", "#3b82f6"], // Green for Income, Red for Expenses, Blue for Delta
        yaxis: {
          labels: {
            formatter: function (val) {
              // Convert to number and round to 2 decimals to remove floating point errors
              const numVal = typeof val === 'number' ? val : parseFloat(val);
              if (isNaN(numVal)) return val;
              
              // Round to 2 decimals to avoid floating point precision issues
              const rounded = Math.round(numVal * 100) / 100;
              
              return new Intl.NumberFormat("en-US", {
                maximumFractionDigits: 2,
                minimumFractionDigits: 0,
              }).format(rounded);
            },
          },
        },
        plotOptions: {
          bar: {
            horizontal: false,
            distributed: true,
            columnWidth: "60%",
          },
        },
        labels: [],
      },
      xaxis: {
        categories: [],
      },
      series: [
        {
          data: [],
        },
      ],
      width: 750,
      type: "bar",
    },
    areaChart: {
      options: {
        title: {
          text: "Budget Breakdown",
          align: "left",
          margin: 2,
          offsetX: 0,
          offsetY: 0,
          floating: false,
          style: {
            fontSize: "20px",
            fontWeight: "bold",
            fontFamily: undefined,
            color: "#053a4b",
          },
        },
        chart: {
          type: "area",
          foreColor: "#053a4b",
          redrawOnParentResize: true,
        },
        tooltip: {
          y: {
            formatter: function (val) {
              // Convert to number and round to 2 decimals to remove floating point errors
              const numVal = typeof val === 'number' ? val : parseFloat(val);
              if (isNaN(numVal)) return val;
              
              // Round to 2 decimals to avoid floating point precision issues
              const rounded = Math.round(numVal * 100) / 100;
              
              return new Intl.NumberFormat("en-US", {
                maximumFractionDigits: 2,
                minimumFractionDigits: 0,
              }).format(rounded);
            },
          },
        },
        dataLabels: {
          enabled: true,
          formatter: function (val) {
            // Convert to number and round to 2 decimals to remove floating point errors
            const numVal = typeof val === 'number' ? val : parseFloat(val);
            if (isNaN(numVal)) return val;
            
            // Round to 2 decimals to avoid floating point precision issues
            const rounded = Math.round(numVal * 100) / 100;
            
            return new Intl.NumberFormat("en-US", {
              maximumFractionDigits: 2,
              minimumFractionDigits: 0,
            }).format(rounded);
          },
        },
        xaxis: {
          categories: ["Income", "Saving", "Fixed", "Expenses", "Free"],
          labels: {
            style: {
              fontSize: "12px",
            },
          },
        },
        fill: {
          type: "gradient",
          gradient: {
            shadeIntensity: 1,
            opacityFrom: 0.7,
            opacityTo: 0.9,
            stops: [0, 90, 100],
          },
        },
        colors: ["#6366f1"], // Single color for area chart
        legend: {
          show: false,
        },
      },
      series: [
        {
          name: "Budget",
          data: [0, 0, 0, 0, 0],
        },
      ],
      width: 750,
      type: "area",
      title: "",
    },
    category: [],
    categoriesWithBudget: {}, // Map of category title to budget info
    screen_height: window.innerHeight,
    screen_width: window.innerWidth,
    loading: true,
  };

  componentDidMount = async () => {
    // Handle window resize - update screen dimensions
    const handleResize = () => {
      this.setState({
        screen_height: window.innerHeight,
        screen_width: window.innerWidth,
      });
    };

    window.addEventListener("resize", handleResize);
    this.handleResize = handleResize;

    let date = new Date();
    let year = date.getFullYear().toString();
    let month = date.getMonth() + 1;
    if (month < 10) {
      month = "0" + month.toString();
    } else {
      month = month.toString();
    }
    let date_yyyymm = year + month;

    await this.loadReportData(date_yyyymm);
    await this.loadCategoriesWithBudget(date_yyyymm);
  };
  componentWillUnmount() {
    if (this.handleResize) {
      window.removeEventListener("resize", this.handleResize);
    }
  }
  render() {
    let slider = (
      <Slider
        screen_width={this.state.screen_width || window.innerWidth}
        screen_height={this.state.screen_height || window.innerHeight}
        screen_2_bar={this.state.radialBar}
        screen_2_area={this.state.areaChart}
        category={this.state.category}
        symbol={this.props.settings?.symbol || ""}
        categoriesWithBudget={this.state.categoriesWithBudget}
      />
    );

    if (this.state.loading) {
      slider = (
        <div className="flex items-center justify-center py-10">
          <Loader />
        </div>
      );
    }

    // Calculate available height: viewport - nav bar (~57px) - datepicker header (64px) - padding (32px total)
    const datePickerHeaderHeight = 64;

    return (
      <div className="w-full flex flex-col relative flex-1 min-h-0 overflow-hidden md:flex-initial md:min-h-full md:h-full md:overflow-visible" style={{ overflowX: "hidden" }}>
        {/* Header with DatePicker - fixed on mobile below main header */}
        <header 
          className="fixed left-0 right-0 z-30 bg-slate-900 text-white shadow flex-shrink-0 rounded-sm md:relative md:z-10" 
          style={{ 
            height: `${datePickerHeaderHeight}px`, 
            borderRadius: "3px",
            top: "50px"
          }}
        >
          <div className="mx-auto flex max-w-7xl items-center justify-center px-4 py-4 sm:px-6 lg:px-8 h-full">
            <DatePicker selectDateHandler={this.selectDateHandler} />
          </div>
        </header>
        
        {/* Spacer for fixed datepicker on mobile */}
        <div className="h-16 flex-shrink-0 md:hidden"></div>

        {/* Content area - flex so Slider gets constrained height and panels scroll independently */}
        <div 
          className="px-4 py-4 md:px-6 flex-1 min-h-0 overflow-hidden md:overflow-hidden flex flex-col"
        >
          {slider}
        </div>
      </div>
    );
  }
  selectDateHandler = async (date) => {
    let yyyymm_arr = date.split("-");
    let yyyymm = yyyymm_arr[1] + yyyymm_arr[0];
    await this.loadReportData(yyyymm);
    await this.loadCategoriesWithBudget(yyyymm);
  };

  loadCategoriesWithBudget = async (yyyymm) => {
    try {
      // Convert YYYYMM to yearMonth for API (it's already in the right format)
      const yearMonth = parseInt(yyyymm);
      const url = `${this.props.baseUrl}${this.props.apiRouts.CATEGORIES}?yearMonth=${yearMonth}`;
      const categories = await API.Get(url);

      if (categories) {
        const categoriesWithBudget = {};
        categories.forEach(cat => {
          if (cat.budget !== null && cat.budget !== undefined) {
            categoriesWithBudget[cat.title] = {
              budget: cat.budget,
              spent: cat.spent || 0,
              remaining: cat.remaining || 0,
              isOverBudget: cat.isOverBudget || false,
            };
          }
        });
        this.setState({ categoriesWithBudget });
      }
    } catch (error) {
      console.error("Error loading categories with budget:", error);
    }
  };

  loadReportData = async (yyyymm) => {
    this.setState({ loading: true });
    
    try {
      const url = `${this.props.baseUrl}${this.props.reportsUrl}?date=${yyyymm}`;
      const reportData = await API.Get(url);

      if (reportData) {
        
        // Transform API response to match component state structure
        const category = reportData.categories.map(cat => ({
          title: cat.title,
          total: roundNumber(cat.total),
          exp_list: cat.expense_list || [],
          type: cat.type,
        }));

        // Update pie chart with server data
        const pieChart = JSON.parse(JSON.stringify(this.state.pieChart));
        pieChart.options.labels = reportData.pie_chart.labels;
        pieChart.series = cleanNumberArray(reportData.pie_chart.series);
        
        // Assign colors to pie chart based on category labels
        if (reportData.pie_chart.labels && reportData.pie_chart.labels.length > 0) {
          pieChart.options.colors = reportData.pie_chart.labels.map(label => getCategoryColor(label));
        }

        // Update bar chart (radialBar) with server data
        const radialBar = JSON.parse(JSON.stringify(this.state.radialBar));
        radialBar.series[0] = { data: cleanNumberArray(reportData.bar_chart.series) };
        radialBar.options.labels = reportData.bar_chart.labels;
        
        // Assign colors to bar chart based on category labels
        if (reportData.bar_chart.labels && reportData.bar_chart.labels.length > 0) {
          radialBar.options.colors = reportData.bar_chart.labels.map(label => getCategoryColor(label));
        }

        // Update area chart with totals data
        const areaChart = JSON.parse(JSON.stringify(this.state.areaChart));
        
        // Calculate area series from categories (Income, Saving, Fixed Expenses, Expenses, Free Budget)
        // Sum all categories by type since there can be multiple categories of the same type
        let areaSeries = [0, 0, 0, 0, 0]; // Income, Saving, Fixed, Expenses, Free
        reportData.categories.forEach(cat => {
          if (cat.type === "income") areaSeries[0] += cat.total;
          else if (cat.type === "saving") areaSeries[1] += cat.total;
          else if (cat.type === "const_expense") areaSeries[2] += cat.total;
          else if (cat.type === "expense") areaSeries[3] += cat.total;
        });
        // Free Budget = Income - Saving - Fixed Expenses - Expenses
        areaSeries[4] = areaSeries[0] - areaSeries[1] - areaSeries[2] - areaSeries[3];
        
        // Clean all numbers to remove floating point errors
        areaChart.series[0].data = cleanNumberArray(areaSeries);
        
        // Update color based on Free Budget value (indigo if positive, red if negative)
        const freeBudgetColor = areaSeries[4] >= 0 ? "#6366f1" : "#ef4444";
        areaChart.options.colors = [freeBudgetColor];

        // Set chart widths and heights based on screen size
        if (this.state.screen_width > 850) {
          pieChart.width = 550;
          radialBar.width = 750;
          radialBar.height = 400;
          areaChart.width = 750;
        } else if (this.state.screen_width > 700) {
          pieChart.width = 500;
          radialBar.width = 500;
          radialBar.height = 350;
          areaChart.width = 500;
        } else if (this.state.screen_width > 500) {
          pieChart.width = 400;
          radialBar.width = 400;
          radialBar.height = 300;
          areaChart.width = 400;
        }
        if (this.state.screen_width < 500) {
          pieChart.width = 300;
          radialBar.width = Math.min(300, this.state.screen_width - 80); // Smaller for mobile
          radialBar.height = Math.min(280, this.state.screen_height - 280); // Fit within viewport minus headers/padding
          areaChart.width = Math.min(280, this.state.screen_width - 80); // Even smaller for area chart to prevent cutoff
        }

        this.setState({
          category,
          pieChart,
          radialBar,
          areaChart,
          loading: false,
        });
      } else {
        this.setState({ loading: false });
      }
    } catch (error) {
      console.error("Error loading report data:", error);
      this.setState({ loading: false });
    }
  };
}
